import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMenu, FiX, FiUser, FiBriefcase, FiToggleLeft, FiToggleRight } = FiIcons;

const Navbar = ({ viewMode, setViewMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/experience', label: 'Experience' },
    { path: '/skills', label: 'Skills' },
    { path: '/projects', label: 'Projects' },
    { path: '/impact', label: 'Impact' },
    { path: '/personal', label: 'Personal' },
    { path: '/contact', label: 'Contact' }
  ];

  const toggleViewMode = () => {
    setViewMode(viewMode === 'professional' ? 'personal' : 'professional');
  };

  // Determine if navbar should use light or dark theme
  const useDarkTheme = !isHomePage || scrolled;

  return (
    <motion.nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        useDarkTheme
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-2'
          : 'bg-transparent py-4'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="font-heading font-bold text-2xl tracking-tight">
            <span className={useDarkTheme ? 'text-teal-600' : 'text-white'}>Gary</span>
            <span className="text-yellow-400"> Fagerholm</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`relative px-4 py-2 text-sm font-bold tracking-wide transition-all duration-200 rounded-lg ${
                    useDarkTheme
                      ? isActive
                        ? 'text-teal-600'
                        : 'text-gray-800 hover:text-teal-600 hover:bg-gray-100'
                      : isActive
                      ? 'text-yellow-400'
                      : 'text-white hover:text-yellow-400 hover:bg-white/10'
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <motion.div
                      className={`absolute bottom-1 left-4 right-4 h-0.5 ${
                        useDarkTheme ? 'bg-teal-600' : 'bg-yellow-400'
                      }`}
                      layoutId="navbar-indicator"
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* View Mode Toggle */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={toggleViewMode}
              className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 border ${
                useDarkTheme
                  ? 'bg-gray-100 border-gray-200 text-gray-800 hover:bg-gray-200'
                  : 'bg-white/10 border-white/20 text-white hover:bg-white/20'
              }`}
            >
              <SafeIcon
                icon={viewMode === 'professional' ? FiBriefcase : FiUser}
                className="w-4 h-4"
              />
              <span className="text-xs font-bold uppercase tracking-widest">
                {viewMode === 'professional' ? 'Pro' : 'Personal'}
              </span>
              <SafeIcon
                icon={viewMode === 'professional' ? FiToggleLeft : FiToggleRight}
                className={`w-5 h-5 ${useDarkTheme ? 'text-teal-600' : 'text-yellow-400'}`}
              />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg transition-colors ${
              useDarkTheme
                ? 'hover:bg-gray-100 text-gray-900'
                : 'hover:bg-white/10 text-white'
            }`}
          >
            <SafeIcon icon={isOpen ? FiX : FiMenu} className="w-6 h-6" />
          </button>
        </div>

        {/* Mobile Navigation */}
        <motion.div
          className={`md:hidden overflow-hidden ${isOpen ? 'block' : 'hidden'}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: isOpen ? 1 : 0, height: isOpen ? 'auto' : 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="py-4 space-y-2 bg-white rounded-2xl shadow-2xl mt-2 border border-gray-100">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`block px-6 py-3 text-sm font-bold transition-colors ${
                  location.pathname === item.path
                    ? 'text-teal-600 bg-teal-50'
                    : 'text-gray-800 hover:text-teal-600 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="px-6 py-4 border-t border-gray-100">
              <button
                onClick={toggleViewMode}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl bg-gray-100 text-gray-800 font-bold"
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon
                    icon={viewMode === 'professional' ? FiBriefcase : FiUser}
                    className="w-5 h-5 text-teal-600"
                  />
                  <span>{viewMode === 'professional' ? 'Professional' : 'Personal'} Mode</span>
                </div>
                <SafeIcon
                  icon={viewMode === 'professional' ? FiToggleLeft : FiToggleRight}
                  className="w-6 h-6 text-teal-600"
                />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.nav>
  );
};

export default Navbar;